import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';

import { AlertService } from 'src/app/services/alert.service';
import { PbxStatusService } from 'src/app/services/pbx-status.service';
import { PbxProvidersResponse, PbxSearchResponse, PbxSpeakerCandidate, PbxSpeakerPick, PbxStatusMonitor } from '../../models/pbx.model';
import { PBX_PROVIDER_LABELS, PBX_STATUS_BADGE, PBX_STATUS_LABELS } from '../../const/pbx';
import { SwSelectComponent } from '../sw-select/sw-select.component';

type CameraMatch = 'saved' | 'exact' | 'default' | 'manual' | 'none';

interface SiteSpeakerRecord {
  _id: string;
  cameraIds: string[];
}

/**
 * "Find speakers on PBX" under a site's speaker numbers. Neither PBX knows sites, but on
 * both the site address is part of the speaker's name, so the search starts from the
 * site name. Emits the picked speaker; the parent decides where its extension goes.
 * Renders nothing when the API has no PBX configured.
 *
 * With a [siteId] (editing a saved site) each speaker also gets a camera of that site,
 * pre-picked only when a camera name equals the speaker name exactly.
 */
@Component({
  selector: 'app-pbx-speaker-suggest',
  imports: [FormsModule, SwSelectComponent],
  templateUrl: './pbx-speaker-suggest.component.html',
  styleUrl: './pbx-speaker-suggest.component.scss'
})
export class PbxSpeakerSuggestComponent implements OnInit, OnDestroy {
  private alertService = inject(AlertService);
  private pbxStatusService = inject(PbxStatusService);

  @Input() siteName = '';
  // Extensions already in the parent's list, shown as "Added".
  @Input() selected: string[] = [];
  // Cameras only exist on a saved site (they are created by its first alert), so the
  // camera column is shown in edit mode only.
  @Input() siteId: string | null = null;
  @Output() add = new EventEmitter<PbxSpeakerPick>();
  // A camera picked (or cleared) for a speaker already in the parent's list: those rows have
  // no Add left to press, but still need their camera saved.
  @Output() cameraChange = new EventEmitter<PbxSpeakerPick>();
  @Output() openChange = new EventEmitter<boolean>();

  // null until the API answers; false = no PBX configured (or the check failed).
  available: boolean | null = null;
  isOpen = false;
  query = '';
  results: PbxSpeakerCandidate[] = [];
  errors: string[] = [];
  searching = false;
  searched = false;
  private preparing = false;
  searchedQuery = '';
  private monitor: PbxStatusMonitor | null = null;

  cameras: { _id: string; label: string }[] = [];
  camerasLoading = false;
  camerasLoaded = false;
  cameraPicks: Record<string, string | null> = {};
  cameraMatches: Record<string, CameraMatch> = {};
  readonly matchLabels: Record<CameraMatch, string> = { saved: 'Saved', exact: 'Matched', default: 'Auto', manual: 'Selected', none: 'Not matched' };
  // This site's Speaker records by provider:extension, so a saved camera shows again.
  private siteRecords: Record<string, SiteSpeakerRecord> = {};

  providerLabels = PBX_PROVIDER_LABELS;
  statusBadge = PBX_STATUS_BADGE;
  statusLabels = PBX_STATUS_LABELS;

  private subs: Subscription[] = [];

  ngOnInit(): void {
    this.subs.push(this.alertService.getPbxProviders().subscribe({
      next: (res: PbxProvidersResponse) => {
        this.available = !!(res && res.providers && res.providers.length);
        if (this.isOpen) this.searchSiteName();
      },
      error: (e) => { this.available = false; console.log(e) }
    }));
    this.subs.push(this.pbxStatusService.monitor$.subscribe((monitor) => this.onMonitor(monitor)));
    this.pbxStatusService.refreshMonitor();
  }

  // The search answers with a VitalPBX speaker's last known status and no stale flag, so a
  // monitor that is down would leave hours-old "Online" rows. Monitor back: search again,
  // since the blanked statuses are not re-sent.
  private onMonitor(monitor: PbxStatusMonitor | null) {
    const wasDown = this.monitor?.connected === false;
    this.monitor = monitor;
    if (monitor?.connected && wasDown && this.searched) this.search();
    else this.results = this.clearStale(this.results);
  }

  private clearStale(list: PbxSpeakerCandidate[]): PbxSpeakerCandidate[] {
    return list.map((c) => c.provider === 'vitalpbx' && this.monitor?.connected === false
      ? { ...c, status: 'unknown' } : c);
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.openChange.emit(this.isOpen);
    this.searchSiteName();
  }

  private searchSiteName() {
    if (!this.isOpen || !this.available || this.searched || this.preparing) return;
    const fallback = (this.siteName || '').trim();
    const run = (query: string) => {
      this.query = query;
      if (this.query.length >= 2) this.search();
    };
    if (!this.siteId) {
      run(fallback);
      return;
    }
    this.preparing = true;
    this.subs.push(this.alertService.getAllSpeakerList({ query: { site: this.siteId } }).subscribe({
      next: (res: any) => {
        this.preparing = false;
        run(this.linkedPbxSite(res?.data ?? res?.list ?? []) || fallback);
      },
      error: (e) => { this.preparing = false; console.log(e); run(fallback); }
    }));
  }

  private linkedPbxSite(list: any[]): string {
    const names = (Array.isArray(list) ? list : [])
      .filter((s: any) => s && s.pbx_provider && s.name)
      .map((s: any) => `${s.name}`.trim().split(/\s+/));
    if (!names.length) return '';
    const common: string[] = [];
    for (let i = 0; i < names[0].length; i++) {
      const word = names[0][i];
      if (!names.every((n) => (n[i] || '').toLowerCase() === word.toLowerCase())) break;
      common.push(word);
    }
    const lead = common.join(' ').split(/\s[-–]\s|[(\[|:,]/)[0].replace(/[\s\-–]+$/, '').trim();
    return lead.split(/\s+/).length >= 2 ? lead : '';
  }

  
  search() {
    const query = (this.query || '').trim();
    if (query.length < 2) return;
    this.searching = true;
    this.subs.push(this.alertService.searchPbxSpeakers(query).subscribe({
      next: (res: PbxSearchResponse) => {
        this.searching = false;
        this.searched = true;
        // Online first: those are the ones worth adding. Name order within each group.
        this.results = this.clearStale((res && res.speakers) || [])
          .sort((a, b) => Number(b.status === 'online') - Number(a.status === 'online') || a.name.localeCompare(b.name));
        this.searchedQuery = query;
        this.errors = ((res && res.errors) || []).map((e) => `${this.providerLabels[e.provider] || e.provider}: ${e.message}`);
        this.loadCameras();
      },
      error: (e) => { this.searching = false; console.log(e) }
    }));
  }

  private loadCameras() {
    if (!this.siteId) return;
    if (this.camerasLoaded) {
      this.autoMatchCameras();
      return;
    }
    this.camerasLoading = true;
    this.subs.push(forkJoin({
      filters: this.alertService.filterMultipleList({ siteIds: [this.siteId] }),
      speakers: this.alertService.getAllSpeakerList({ query: { site: this.siteId } }),
    }).subscribe({
      next: ({ filters, speakers }: any) => {
        this.camerasLoading = false;
        this.camerasLoaded = true;
        this.cameras = ((filters && filters.cameras) || [])
          .map((c: any) => ({ _id: c._id, label: c.display_name || c.name || '' }))
          // Numeric order ("Camera 2" before "Camera 10"): the Auto defaults follow it.
          .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        const list = speakers?.data ?? speakers?.list ?? [];
        this.siteRecords = (Array.isArray(list) ? list : [])
          .filter((s: any) => s && s.pbx_provider && s.call_number)
          .reduce((acc: Record<string, SiteSpeakerRecord>, s: any) => {
            acc[`${s.pbx_provider}:${s.call_number}`] = {
              _id: s._id,
              cameraIds: (s.camera || []).map((c: any) => `${c?._id ?? c}`),
            };
            return acc;
          }, {});
        this.autoMatchCameras();
      },
      error: (e) => { this.camerasLoading = false; console.log(e) }
    }));
  }

  // The site's own Speaker record for this candidate, if it has one.
  recordOf(candidate: PbxSpeakerCandidate): SiteSpeakerRecord | null {
    return this.siteRecords[this.keyOf(candidate)] || null;
  }

  // Linked to a Speaker record that belongs to some other site: left alone here.
  isBoundElsewhere(candidate: PbxSpeakerCandidate): boolean {
    return !!candidate.already_bound_to && !this.recordOf(candidate);
  }

  // A saved camera wins, then an exact name match (case, spacing and punctuation ignored).
  // Whatever is still empty gets a default by position: the first such card takes the first
  // camera not already used, and so on. Defaults are badged "Auto" for the admin to check.
  private autoMatchCameras() {
    const norm = (text: string) => this.words(text).join(' ');
    const siteCameraIds = new Set(this.cameras.map((c) => c._id));
    const unmatched: PbxSpeakerCandidate[] = [];
    this.results.forEach((candidate) => {
      const key = this.keyOf(candidate);
      if (this.cameraPicks[key] !== undefined) return;
      const record = this.recordOf(candidate);
      if (record) {
        const saved = record.cameraIds.find((id) => siteCameraIds.has(id)) || null;
        this.cameraPicks[key] = saved;
        this.cameraMatches[key] = saved ? 'saved' : 'none';
        if (!saved) unmatched.push(candidate);
        return;
      }
      const names = [norm(this.shortName(candidate.name)), norm(candidate.name)];
      const camera = this.cameras.find((c) => names.includes(norm(c.label)));
      this.cameraPicks[key] = camera ? camera._id : null;
      this.cameraMatches[key] = camera ? 'exact' : 'none';
      if (camera) this.emitCameraIfAdded(candidate);
      else if (!this.isBoundElsewhere(candidate)) unmatched.push(candidate);
    });

    const used = new Set(Object.values(this.cameraPicks).filter((id): id is string => !!id));
    const free = this.cameras.filter((c) => !used.has(c._id));
    unmatched.forEach((candidate, index) => {
      const camera = free[index];
      if (!camera) return;
      const key = this.keyOf(candidate);
      this.cameraPicks[key] = camera._id;
      this.cameraMatches[key] = 'default';
      this.emitCameraIfAdded(candidate);
    });
  }

  onCameraChange(candidate: PbxSpeakerCandidate, cameraId: string | null) {
    const key = this.keyOf(candidate);
    this.cameraPicks[key] = cameraId || null;
    this.cameraMatches[key] = cameraId ? 'manual' : 'none';
    this.emitCameraIfAdded(candidate);
  }

  private emitCameraIfAdded(candidate: PbxSpeakerCandidate) {
    if (!this.isAdded(candidate) || this.isBoundElsewhere(candidate)) return;
    this.cameraChange.emit(this.pickOf(candidate));
  }

  private pickOf(candidate: PbxSpeakerCandidate): PbxSpeakerPick {
    const record = this.recordOf(candidate);
    const siteCameraIds = new Set(this.cameras.map((c) => c._id));
    return {
      ...candidate,
      camera_id: this.siteId && !this.isBoundElsewhere(candidate) ? this.cameraPicks[this.keyOf(candidate)] ?? null : null,
      speaker_id: record ? record._id : null,
      other_camera_ids: record ? record.cameraIds.filter((id) => !siteCameraIds.has(id)) : [],
    };
  }

  keyOf(candidate: PbxSpeakerCandidate): string {
    return `${candidate.provider}:${candidate.extension}`;
  }

  addCandidate(candidate: PbxSpeakerCandidate) {
    this.add.emit(this.pickOf(candidate));
  }

  isAdded(candidate: PbxSpeakerCandidate): boolean {
    return this.selected.includes(candidate.extension);
  }

  get hasKnownStatus(): boolean {
    return this.results.some((r) => r.status !== 'unknown');
  }

  get onlineCount(): number {
    return this.results.filter((r) => r.status === 'online').length;
  }

  get offlineCount(): number {
    return this.results.filter((r) => r.status === 'offline').length;
  }

  get addableOnline(): PbxSpeakerCandidate[] {
    return this.results.filter((r) => r.status === 'online' && !this.isAdded(r));
  }

  addAllOnline() {
    this.addableOnline.forEach((candidate) => this.addCandidate(candidate));
  }

  private words(text: string): string[] {
    return (text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  }

  // Every result starts with the site address that was searched; dropping it leaves the
  // part that tells the horns apart ("28th Fl - Horn 1"). The full name stays in the tooltip.
  shortName(name: string): string {
    const words = (text: string) => this.words(text);
    const queryWords = words(this.searchedQuery);
    const tokens = name.split(/\s+/);
    let matched = 0;
    while (matched < tokens.length && matched < queryWords.length
      && words(tokens[matched]).join(' ') === queryWords[matched]) {
      matched++;
    }
    if (matched < queryWords.length) return name;
    const rest = tokens.slice(matched).join(' ').replace(/^[\s\-–|:,]+/, '').trim();
    return rest || name;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
